import { useState, useEffect } from "react";
import { toast } from "sonner";
import { p, g, TENANT_KEY } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Building2, KeyRound, Wand2, ArrowLeft } from "lucide-react";
import { useI18n } from "@/i18n";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { SetupWizard } from "@/pages/Setup";
import { RecuperaCodice } from "@/components/RecuperaCodice";

export default function TenantLogin({ onLogged, onBack }) {
  const { t } = useI18n();
  const [slug, setSlug] = useState("");
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [quiz, setQuiz] = useState(false);
  const [recover, setRecover] = useState(false);
  const [venue, setVenue] = useState(null);

  // Link «ho perso il codice»: apre la sessione senza digitare nulla
  useEffect(() => {
    const token = new URLSearchParams(window.location.search).get("r");
    if (!token) return;
    p("/tenant/recover/use", { token })
      .then((r) => {
        localStorage.setItem(TENANT_KEY, r.token);
        toast.success(t("rec.entered", { name: r.tenant?.name || r.slug }));
        window.history.replaceState({}, "", "/entra");
        onLogged?.(r);
      })
      .catch(() => toast.error(t("rec.expired")));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Appena riconosciamo il codice attività mostriamo insegna, logo e motto del locale.
  useEffect(() => {
    const s = slug.trim();
    if (s.length < 3) return setVenue(null);
    const id = setTimeout(async () => {
      try {
        setVenue(await g("/tenant/identity", { slug: s }));
      } catch {
        setVenue(null);
      }
    }, 400);
    return () => clearTimeout(id);
  }, [slug]);

  const enter = async () => {
    if (!slug.trim() || !code.trim()) return toast.error("Inserisci nome attività e codice");
    setBusy(true);
    try {
      const r = await p("/tenant/login", { slug, code });
      localStorage.setItem(TENANT_KEY, r.token);
      toast.success(`Benvenuto in ${r.tenant.name}`);
      onLogged(r.tenant);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Accesso non riuscito");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-slate-900 px-4" data-testid="tenant-login">
      <Card className="w-full max-w-md p-8">
        <p className="text-[10px] font-bold uppercase tracking-[0.28em] text-slate-500">{t("login.title")}</p>
        <h1 className="font-head mt-1 text-3xl font-bold">Reprehendo <span className="text-slate-400">(RH)</span></h1>
        <p className="mt-3 text-sm text-slate-500">{t("login.sub")}</p>

        {venue && (
          <div className="mt-5 flex items-center gap-3 rounded-lg border border-amber-200 bg-amber-50/70 p-3 fade-up" data-testid="login-venue-card">
            {venue.logo && <img src={venue.logo} alt="" className="h-12 w-12 shrink-0 rounded-md bg-white object-contain p-1" data-testid="login-venue-logo" />}
            <div className="min-w-0">
              <p className="truncate font-head text-base font-bold text-slate-900" data-testid="login-venue-name">{venue.name}</p>
              {venue.motto && (
                <p className="mt-0.5 text-xs italic text-slate-600" data-testid="login-venue-motto">«{venue.motto}»</p>
              )}
            </div>
          </div>
        )}

        <div className="mt-6 space-y-3">
          <div className="relative">
            <Building2 className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <Input
              className="h-12 pl-9"
              placeholder={t("login.name")}
              value={slug}
              onChange={(e) => setSlug(e.target.value)}
              data-testid="tenant-slug-input"
            />
          </div>
          <div className="relative">
            <KeyRound className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <Input
              className="h-12 pl-9 uppercase tracking-[0.2em]"
              placeholder={t("login.code")}
              value={code}
              onChange={(e) => setCode(e.target.value.toUpperCase())}
              onKeyDown={(e) => e.key === "Enter" && enter()}
              data-testid="tenant-code-input"
            />
          </div>
          <Button className="h-12 w-full" disabled={busy} onClick={enter} data-testid="tenant-login-btn">
            {t("login.btn")}
          </Button>
        </div>

        <button
          onClick={() => setRecover(true)}
          data-testid="lost-code-btn"
          className="mt-4 w-full text-center text-xs font-semibold text-slate-500 underline hover:text-slate-800"
        >
          {t("login.lost")}
        </button>

        <button
          onClick={() => setQuiz(true)}
          data-testid="public-quiz-btn"
          className="mt-5 flex w-full items-center justify-center gap-2 rounded-md border border-slate-200 py-3 text-sm font-semibold text-slate-700 transition-colors hover:border-slate-400"
        >
          <Wand2 className="h-4 w-4" /> {t("login.quiz")}
        </button>

        {onBack && (
          <button onClick={onBack} data-testid="back-to-landing-btn" className="mt-4 flex w-full items-center justify-center gap-1.5 text-xs font-semibold text-slate-500 hover:text-slate-800">
            <ArrowLeft className="h-3.5 w-3.5" /> {t("login.back")}
          </button>
        )}
      </Card>

      <RecuperaCodice open={recover} onOpenChange={setRecover} />

      <Dialog open={quiz} onOpenChange={setQuiz}>
        <DialogContent className="max-h-[92vh] max-w-2xl overflow-y-auto p-0 [&>button]:hidden" data-testid="public-quiz-dialog">
          <DialogTitle className="sr-only">{t("login.quiz")}</DialogTitle>
          <DialogDescription className="sr-only">{t("wiz.public")}</DialogDescription>
          <SetupWizard publicMode onClose={() => setQuiz(false)} />
        </DialogContent>
      </Dialog>
    </div>
  );
}
